import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { getIn, useFormikContext } from 'formik';

import { QUERY_KEY, WARNING_DIALOG } from '~common';
import {
  AutoCompleteSingleField,
  AutoCompleteSingleFieldWithNone,
  PaddingBox,
  WarningDialog,
} from '~components/atoms';
import { AddressField, PhoneField, TextField } from '~components/atoms/form';
import { useCheckVenueExist } from '~hooks';
import { GeolocationProvider } from '~providers';
import {
  PINNING_STEP,
  usePinningStepContext,
  VENUE_CONTACT_STEPS,
} from '~providers/PinningStepProvider';
import { IFormCreationValues } from '~providers/useForm';
import { RECORD_STATUS } from '~types';

export const CreateVenueStep: React.FC = () => {
  const { pinningStep, goToNextStep, selectedGooglePlaceId, skipVenueContact } =
    usePinningStepContext();

  const { values, errors, touched } = useFormikContext<IFormCreationValues>();
  const [currentFields, setCurrentFields] = useState<string[]>([]);

  useEffect(() => {
    switch (pinningStep) {
      case PINNING_STEP.CREATE_VENUE_ADD_NAME:
        setCurrentFields(['venue.locationName']);
        return;
      case PINNING_STEP.CREATE_VENUE_ADD_VENUE_TYPE:
        setCurrentFields(['venue.venueType']);
        return;
      case PINNING_STEP.CREATE_VENUE_ADD_VENUE_SUB_TYPE:
        setCurrentFields(['venue.venueSubType']);
        return;
      case PINNING_STEP.CREATE_VENUE_ADD_ADDRESS:
        setCurrentFields([
          'venue.address.name',
          'venue.address.location.lat',
          'venue.address.location.lng',
          'venue.address.location.infoAddress.city',
          'venue.address.location.infoAddress.state',
          'venue.address.location.infoAddress.zip',
          'venue.address.location.infoAddress.country',
          'venue.address.location.infoAddress.countryCode',
        ]);
        return;
      case PINNING_STEP.CREATE_VENUE_ADD_CONTACT:
        setCurrentFields([
          'venue.venueContact.firstName',
          'venue.venueContact.lastName',
          'venue.venueContact.role',
        ]);
        return;
      case PINNING_STEP.CREATE_VENUE_ADD_EMAIL:
        setCurrentFields(['venue.venueContact.email']);
        return;
      case PINNING_STEP.CREATE_VENUE_ADD_PHONE_NUM:
        setCurrentFields(['venue.venueContact.phone']);
        return;
      default:
        return;
    }
  }, [pinningStep]);

  const areFieldsComplete = useMemo(() => {
    // Check if all fields in currentFields have values and no errors in the formik values
    return currentFields.every((field) => {
      // Extract the value from formik.values
      const fieldValue = field.split('.').reduce(
        (acc, key) => acc?.[key as keyof typeof acc],
        values as unknown, // formik.values here
      );

      // Extract the error from formik.errors
      const fieldError = field.split('.').reduce(
        (acc, key) => acc?.[key as keyof typeof acc],
        errors as unknown, // formik.errors here
      );

      const fieldTouched = field.split('.').reduce(
        (acc, key) => acc?.[key as keyof typeof acc],
        touched as unknown, // formik.touched here
      );

      // For venue sub-type, it's optional so we only check for errors, not values
      // Also consider "None" as a valid default value
      if (field === 'venue.venueSubType') {
        return (
          !fieldError &&
          (!!fieldValue ||
            (fieldValue &&
              typeof fieldValue === 'object' &&
              'name' in fieldValue &&
              fieldValue.name === 'None'))
        );
      }

      // Check if the field has a value and no associated errors
      return !!fieldValue && !(fieldTouched && fieldError);
    });
  }, [currentFields, values, errors, touched]); // Include 'errors' in the dependencies array

  const hasEmptyField = currentFields.some((field) => !getIn(values, field));

  const getAddressErrorMessage = () => {
    return (
      errors?.venue?.address?.name ??
      errors?.venue?.address?.location?.lat ??
      errors?.venue?.address?.location?.lng ??
      errors?.venue?.address?.location?.infoAddress?.city ??
      errors?.venue?.address?.location?.infoAddress?.state ??
      errors?.venue?.address?.location?.infoAddress?.zip ??
      errors?.venue?.address?.location?.infoAddress?.country ??
      errors?.venue?.address?.location?.infoAddress?.countryCode
    );
  };

  const {
    isExistedVenueAlertOpen,
    closeExistedVenueAlert,
    validateVenue,
    isFetchingVenueCheck,
  } = useCheckVenueExist({
    venueName: values.venue?.locationName ?? '',
    venueAddress: values.venue?.address.name ?? '',
  });

  const handleGoToNextStep = useCallback(() => {
    if (pinningStep === PINNING_STEP.CREATE_VENUE_ADD_ADDRESS) {
      validateVenue(goToNextStep);
    } else {
      goToNextStep();
    }
  }, [goToNextStep, pinningStep, validateVenue]);

  return (
    <>
      <PaddingBox
        flex={1}
        sx={{
          bgcolor: 'background.paper',
          width: '100%',
          height: '100%',
        }}
      >
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          gap={2}
          width="100%"
        >
          {pinningStep === PINNING_STEP.CREATE_VENUE_ADD_NAME && (
            <>
              <Typography variant="h6" fontWeight={600}>
                {selectedGooglePlaceId
                  ? 'Confirm your venue'
                  : 'What’s the name of this venue?'}
              </Typography>
              <TextField
                label={
                  <Typography variant="body2" fontWeight={400}>
                    Venue Name
                  </Typography>
                }
                name="venue.locationName"
                disabled={!!selectedGooglePlaceId}
              />
              {selectedGooglePlaceId ? (
                <Stack gap={0.5}>
                  <Typography variant="h3" fontSize={16} fontWeight={600}>
                    Address
                  </Typography>
                  <Typography variant="body1">
                    {values.venue?.address.name}
                  </Typography>
                </Stack>
              ) : null}
            </>
          )}
          {pinningStep === PINNING_STEP.CREATE_VENUE_ADD_VENUE_TYPE && (
            <>
              <Typography variant="h6" fontWeight={600}>
                What type of venue is this?
              </Typography>
              <AutoCompleteSingleField
                required={true}
                placeholder="Search venue type"
                name="venue.venueType"
                path={'api/venue-type/list'}
                bodyRequest={{ status: RECORD_STATUS.ACTIVE }}
                queryKey={[QUERY_KEY.GET_LIST_VENUE_TYPE]}
              />
            </>
          )}
          {pinningStep === PINNING_STEP.CREATE_VENUE_ADD_VENUE_SUB_TYPE && (
            <>
              <Typography variant="h6" fontWeight={600}>
                Venue Sub-Type
              </Typography>
              <AutoCompleteSingleFieldWithNone
                required={false}
                placeholder="Search venue sub-type"
                name="venue.venueSubType"
                path={'api/venue-type/list'}
                bodyRequest={{ status: RECORD_STATUS.ACTIVE }}
                queryKey={[QUERY_KEY.GET_LIST_VENUE_TYPE]}
              />
            </>
          )}
          {pinningStep === PINNING_STEP.CREATE_VENUE_ADD_ADDRESS && (
            <>
              <Typography variant="h6" fontWeight={600}>
                Enter venue’s address
              </Typography>
              <GeolocationProvider>
                <AddressField
                  placeholder="Search venue address"
                  customError={getAddressErrorMessage()}
                  name="venue.address"
                  isRemoveAddress={true}
                  addressInfo={values.venue?.address.name}
                />
              </GeolocationProvider>
            </>
          )}
          {pinningStep === PINNING_STEP.CREATE_VENUE_ADD_CONTACT && (
            <>
              <Typography variant="h6" fontWeight={600}>
                Venue’s contact title
              </Typography>
              <TextField
                label={
                  <Typography variant="body2" fontWeight={400}>
                    First Name
                  </Typography>
                }
                name="venue.venueContact.firstName"
              />
              <TextField
                label={
                  <Typography variant="body2" fontWeight={400}>
                    Last Name
                  </Typography>
                }
                name="venue.venueContact.lastName"
              />
              <TextField
                label={
                  <Typography variant="body2" fontWeight={400}>
                    Title / Role
                  </Typography>
                }
                name="venue.venueContact.role"
                defaultHelperText={'e.g. General Manager'}
              />
            </>
          )}
          {pinningStep === PINNING_STEP.CREATE_VENUE_ADD_EMAIL && (
            <>
              <Typography variant="h6" fontWeight={600}>
                Venue’s contact email
              </Typography>
              <TextField
                label={
                  <Typography variant="body2" fontWeight={400}>
                    Email Address
                  </Typography>
                }
                name="venue.venueContact.email"
              />
            </>
          )}
          {pinningStep === PINNING_STEP.CREATE_VENUE_ADD_PHONE_NUM && (
            <>
              <Typography variant="h6" fontWeight={600}>
                Venue’s contact phone number
              </Typography>
              <PhoneField name="venue.venueContact.phone" />
            </>
          )}
        </Box>
      </PaddingBox>
      <PaddingBox
        alignItems="center"
        bgcolor="background.paper"
        position="fixed"
        bottom={0}
        width="100%"
      >
        <Button
          fullWidth
          size="large"
          variant="contained"
          style={{ borderRadius: '100px' }}
          disabled={!areFieldsComplete || isFetchingVenueCheck}
          onClick={handleGoToNextStep}
        >
          <Typography variant="body2" fontWeight={500}>
            Continue
          </Typography>
        </Button>
        {/* Shown only while a field on this screen is empty; a filled but invalid field shows its error instead */}
        {VENUE_CONTACT_STEPS.includes(pinningStep) && hasEmptyField && (
          <Button
            fullWidth
            size="large"
            variant="text"
            sx={{ mt: 1, borderRadius: '100px' }}
            onClick={skipVenueContact}
          >
            <Typography variant="body2" fontWeight={500}>
              Skip for now
            </Typography>
          </Button>
        )}
      </PaddingBox>

      {/* ALERT VENUE NAME OR ADDRESS EXISTED */}
      <WarningDialog
        open={isExistedVenueAlertOpen}
        onClose={closeExistedVenueAlert}
        aria-labelledby="venue-existed-alert-dialog"
        title={WARNING_DIALOG.VENUE_EXISTED.TITLE}
        content={WARNING_DIALOG.VENUE_EXISTED.CONTENT}
      />
    </>
  );
};
